import { FC, useState, useEffect } from 'react';
import { UseFieldArrayPrepend } from 'react-hook-form';
import toast from 'react-hot-toast';

import ModalDialog from '../../ModalDialog';
import ToastAlert from '../../ToastAlert';

import {
  ActivityListDataFragment,
  useStandardAssessmentsQuery,
} from '../../../../generated/graphql';

import { FollowUpForm } from '../../../lib/followUp';
import {
  formatProviderActivityForFollowUp,
  ProviderActivityObject,
} from '../../../lib/providerActivity';
import { getSelectedProviderActivitiesList } from '../../Library/helpers';
import { isProviderOnlyAssessment } from '../../../lib/assessments';
import { customToast } from '../../ToastAlert/customToast';

import Button from '../../Button';
import TableContainer from '../../TableContainer';
import StandardAssessmentsTable from '../../Library/StandardAssessmentsTable';
import ProviderActivitySlideover from '../../Library/ProviderActivitySlideover';
import { MODAL_TRANSITION_DURATION } from '../../Modal';

type AddStandardAssessmentsModalProps = {
  isModalOpen: boolean;
  setClosed: () => void;
  onAddProviderActivities?: (
    providerActivities: ActivityListDataFragment[],
  ) => void;
  prependToFollowUp?: UseFieldArrayPrepend<FollowUpForm, 'activities'>;
};

const AddStandardAssessmentsModal: FC<AddStandardAssessmentsModalProps> = ({
  isModalOpen,
  setClosed,
  onAddProviderActivities,
  prependToFollowUp,
}) => {
  const [selectedProviderActivity, setSelectedProviderActivity] = useState<
    ActivityListDataFragment | undefined
  >();
  const [isProviderActivitySlideoverOpen, setIsProviderActivitySlideoverOpen] =
    useState(false);

  const {
    data: standardAssessmentsData,
    loading: standardAssessmentsLoading,
    error: standardAssessmentsError,
  } = useStandardAssessmentsQuery();

  const [
    selectedProviderActivitiesObject,
    setSelectedProviderActivitiesObject,
  ] = useState<ProviderActivityObject>({});

  useEffect(() => {
    if (standardAssessmentsError) {
      toast.custom(({ visible }) => (
        <ToastAlert
          isVisible={visible}
          message="Unable to fetch standard assessments."
          level="error"
        />
      ));
    }
  }, [standardAssessmentsError]);

  const selectedProviderActivitiesList = getSelectedProviderActivitiesList(
    selectedProviderActivitiesObject,
  );

  const closeAndResetModal = () => {
    setClosed();
    setTimeout(() => {
      setSelectedProviderActivitiesObject({});
    }, MODAL_TRANSITION_DURATION);
  };

  const handleAttachAssessments = () => {
    // Filter out provider-only assessments - they cannot be sent via follow-ups
    const followUpEligibleActivities = selectedProviderActivitiesList.filter(
      (providerActivity) => {
        if (providerActivity.__typename === 'ActivityAssessment') {
          return !isProviderOnlyAssessment(providerActivity.assessment);
        }
        return true;
      },
    );

    if (
      followUpEligibleActivities.length !==
      selectedProviderActivitiesList.length
    ) {
      customToast.warning(
        'Some forms are provider-only and cannot be sent via follow-ups. They have been excluded.',
      );
    }

    const formattedProviderActivities = followUpEligibleActivities.map(
      (providerActivity, index) =>
        formatProviderActivityForFollowUp(providerActivity, index),
    );

    formattedProviderActivities.forEach((activity) => {
      prependToFollowUp?.(activity);
    });

    onAddProviderActivities?.(followUpEligibleActivities);

    closeAndResetModal();
  };

  const sendProviderActivitiesInFollowUp = (
    providerActivities: ActivityListDataFragment[],
  ) => {
    // Filter out provider-only assessments - they cannot be sent via follow-ups
    const followUpEligibleActivities = providerActivities.filter(
      (providerActivity) => {
        if (providerActivity.__typename === 'ActivityAssessment') {
          return !isProviderOnlyAssessment(providerActivity.assessment);
        }
        return true;
      },
    );

    if (followUpEligibleActivities.length !== providerActivities.length) {
      customToast.warning(
        'This form is provider-only and cannot be sent via follow-ups.',
      );
      return;
    }

    const formattedProviderActivities = followUpEligibleActivities.map(
      (providerActivity, index) =>
        formatProviderActivityForFollowUp(providerActivity, index),
    );

    formattedProviderActivities.forEach((activity) => {
      prependToFollowUp?.(activity);
    });

    onAddProviderActivities?.(followUpEligibleActivities);

    closeAndResetModal();
  };

  return (
    <>
      <ModalDialog
        title="Attach Standard Assessments"
        isOpen={isModalOpen}
        setClosed={closeAndResetModal}
        overflow="auto"
        fullHeight
        isBlocking={isProviderActivitySlideoverOpen}
        headerClassName="py-2"
        leftFooter={
          <>
            {Boolean(selectedProviderActivitiesList.length) ? (
              <div className="text-caption font-medium text-neutral-125">{`${
                selectedProviderActivitiesList.length
              } assessment${
                selectedProviderActivitiesList.length > 1 ? 's' : ''
              } selected`}</div>
            ) : (
              <div />
            )}
          </>
        }
        rightFooter={
          <Button
            title={`Attach assessment${
              selectedProviderActivitiesList?.length > 1 ? 's' : ''
            }`}
            onClick={handleAttachAssessments}
            disabled={!selectedProviderActivitiesList.length}
          />
        }
        bodyClassName="flex min-h-0 w-full flex-col overflow-hidden p-0 pl-6 pr-6 pt-4"
      >
        <TableContainer containerClassName="min-h-0 flex-1">
          <StandardAssessmentsTable
            assessments={standardAssessmentsData?.standardAssessments || []}
            setSelectedProviderActivity={setSelectedProviderActivity}
            setIsProviderActivitySlideoverOpen={
              setIsProviderActivitySlideoverOpen
            }
            sendProviderActivitiesInFollowUp={sendProviderActivitiesInFollowUp}
            showEmptyTableFilterState={
              !standardAssessmentsLoading &&
              standardAssessmentsData?.standardAssessments?.length === 0
            }
            followUpMode
            selectedProviderActivitiesObject={selectedProviderActivitiesObject}
            setSelectedProviderActivitiesObject={
              setSelectedProviderActivitiesObject
            }
          />
        </TableContainer>
      </ModalDialog>
      <ProviderActivitySlideover
        isOpen={isProviderActivitySlideoverOpen}
        onClose={() => setIsProviderActivitySlideoverOpen(false)}
        selectedProviderActivity={selectedProviderActivity}
        followUpMode
      />
    </>
  );
};

export default AddStandardAssessmentsModal;
